import React, { useRef, useState } from 'react'
import { AxiosError } from 'axios'

import { Popover } from '@/components/Popover'
import { Button } from '@/components/Button'
import { Input } from '@/components/Input'
import { IconContainer } from '@/components/IconContainer'
import { Avatar } from '@/components/Avatar'
import { Close } from '@/components/Close'
import { ErrorMsg } from '@/components/ErrorMsg'
import { useMe } from '@/hooks/useMe'
import { api } from '@/utils/api'
import { GoogleAccount, User } from '@/types'

import s from './ShareLinkPopover.module.css'

type Props = {
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onReset: () => void
}

type AccountExtended = GoogleAccount & {
  user: User
}

export const ShareLinkPopover = ({ onChange }: Props) => {
  const { data: me } = useMe()
  const [inputValue, setInputValue] = useState('')
  const [showSharePopup, setShowSharePopup] = useState(false)
  const [users, setUsers] = useState<User[]>([])
  const [emailList, setEmailList] = useState<string[]>([])
  const [isFetching, setIsFetching] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const [error, setError] = useState('')

  const handleAddEmail = async () => {
    try {
      const userEmails =
        (me as { accounts: AccountExtended[] })?.accounts.map(
          (a) => a.user.email
        ) ?? []
      if (userEmails.includes(inputValue)) {
        setError('Sharing with yourself is not allowed.')
        return
      }
      setIsFetching(true)
      const { data } = await api.get(`/users?email=${inputValue}`)
      setUsers([...users, data.data])
      const updatedEmailList = [...emailList, inputValue]
      setEmailList(updatedEmailList)
      onChange({
        target: {
          name: 'shareWith',
          value: updatedEmailList.join(','),
        },
      } as React.ChangeEvent<HTMLInputElement>)
      inputRef.current?.focus()
      setInputValue('')
    } catch (e) {
      const status = (e as AxiosError).response?.status
      if (status === 404) {
        setError('User not found')
      } else if (status === 400) {
        setError('Please enter a valid email')
      }
    } finally {
      setIsFetching(false)
    }
  }

  const handleRemoveUser = (email: string) => {
    setEmailList(emailList.filter((item) => item !== email))
    setUsers(users.filter((u) => u.email !== email))
  }

  return (
    <Popover open={showSharePopup} onOpenChange={setShowSharePopup}>
      <Popover.Trigger>
        <Button>Share</Button>
      </Popover.Trigger>
      <Popover.Content title="Share with" position="topLeft">
        <div className={s.content}>
          <div className={s.fieldset}>
            <Input
              ref={inputRef}
              type="email"
              placeholder="Email address"
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.target.value)
                setError('')
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleAddEmail()
                }
              }}
              required
              autoFocus
              fullWidth
            />
            <Button
              variant="primary"
              type="button"
              disabled={isFetching}
              onClick={handleAddEmail}
            >
              Add
            </Button>
          </div>
          {error && <ErrorMsg text={error} matter={false} />}
          {users.map((u) => (
            <div className={s.item} key={u.id}>
              <IconContainer size="lg">
                <Avatar src={u.image} name={u.name} />
              </IconContainer>
              <span className={s.text}>
                <b>{u.name}</b>
                <div className={s.email}>{u.email}</div>
                <input type="hidden" name="shareWith" value={u.email} />
              </span>
              <Close onClick={() => handleRemoveUser(u.email)} />
            </div>
          ))}
        </div>
      </Popover.Content>
    </Popover>
  )
}
